import { gsap } from 'gsap'

import { deepMerge, getTargetOffset, getTargetOffsetTop, setElementHide } from 'js/utils/Utils'
import { clamp, lerp } from 'js/utils/Maths'

import BasePlaneMesh from 'js/webgl/customMeshes/base/BasePlaneMesh'
import Common from 'js/_core/Common'

import VertexShader from 'js/webgl/_glsl/vert/bgXLPlaneMesh.vert'
import FragmentShader from 'js/webgl/_glsl/frag/bgXLPlaneMesh.frag'

const defaults = {
  instanceName: 'bgXLPlaneMesh',
  $names: {
    parallaxSection: '[data-gl-parallax-section]',
    parallaxCoverTrigger: '[data-gl-parallax-cover-trigger]',
  },
  effectInfo: {
    parallaxScale: 1.1,
    lerpParallax: 0.15,
    lerpCoverParallax: 0.15,
  },
}

export default class BgXLPlaneMesh extends BasePlaneMesh {
  $parallaxSection = null
  $parallaxCoverTrigger = null

  width = 0
  height = 0
  wh = 0
  parallaxSectionTop = 0
  parallaxSectionHeight = 0

  parallaxCoverTriggerTop = 0

  progressLerpParallax = 0
  progressLerpCoverParallax = 0

  originPos = {
    x: 0,
    y: 0,
  }
  meshOffset = {
    x: 0,
    y: 0,
  }

  isFV = false
  isInView = false
  inViewAnimated = false

  constructor(options = {}) {
    super({
      vs: VertexShader,
      fs: FragmentShader,
    })

    this.webgl = null
    this.uniforms = this.uniforms || {}

    deepMerge(this, defaults, options)
  }

  init() {
    this._set$()
    super.init()
    this._setUniforms()
    this._setBaseSize()
    this.setResolutions()
    setElementHide(this.$target)
    this.setTexture()

    Common.webgl.stage.scene.add(this.mesh)
  }

  destroy() {
    if (this.material.uniforms.uTexture && this.material.uniforms.uTexture.value) {
      this.material.uniforms.uTexture.value.dispose()
      this.material.uniforms.uTexture.value = null
    }

    if (this.mesh.material && this.mesh.material.uniforms.uTexture && this.mesh.material.uniforms.uTexture.value) {
      this.mesh.material.uniforms.uTexture.value.dispose()
      this.mesh.material.uniforms.uTexture.value = null
    }

    if (this.uniforms.uTexture && this.uniforms.uTexture.value) {
      this.uniforms.uTexture.value.dispose()
      this.uniforms.uTexture.value = null
    }

    super.destroy()
  }

  _set$() {
    if (this.$target) {
      this.$parallaxSection = this.$target.closest(this.$names.parallaxSection)
      if (this.$parallaxSection) this.$parallaxCoverTrigger = this.$parallaxSection.querySelector(this.$names.parallaxCoverTrigger)
    }
  }

  _setUniforms() {
    deepMerge(this.uniforms, {
      uMeshOffset: {
        value: {
          x: 0,
          y: 0,
        },
      },
      uTexture: { value: null },
      uTextureResolution: {
        value: {
          x: 1,
          y: 1,
        },
      },
      uScaleParallax: {
        value: this.effectInfo.parallaxScale,
      },
      uStickyProgress: {
        value: 0,
      },
      uParallaxBeforeProgress: {
        value: 0,
      },
      uParallaxProgress: {
        value: 0,
      },
      uParallaxProgressLerp: {
        value: 0,
      },
      uStickyAmount: {
        value: 0,
      },
      uScroll: {
        value: 0,
      },
      uParallaxCoverProgress: {
        value: 0,
      },
      uFvInViewScale: {
        value: this.isFV ? 1 : 0,
      },
    })
  }

  setTexture() {
    const texturePath = this.$target.dataset['glImg']

    const textureList = Common.textureList['bgXL']
    const listLength = textureList.length

    for (let i = 0; i < listLength; i++) {
      const texture = textureList[i]
      if (texture.name === texturePath) {
        if (texturePath === texture.name) {
          let width = 1
          let height = 1

          if (texture.image) {
            width = texture.image.naturalWidth
            height = texture.image.naturalHeight
          }

          this.uniforms.uTextureResolution.value.x = width
          this.uniforms.uTextureResolution.value.y = height
          requestAnimationFrame(() => {
            this.uniforms.uTexture.value = texture
          })
        }
        break
      }
    }
  }

  setResolutions() {
    if (this.$target) {
      const rect = this.$target.getBoundingClientRect()
      this.width = rect.width
      this.height = rect.height

      this.uniforms.uMeshResolution.value.x = this.width
      this.uniforms.uMeshResolution.value.y = this.height

      this.originPos.x = -(Common.webgl.stage.stageSize.width * 0.5) + this.width * 0.5
      this.originPos.y = Common.webgl.stage.stageSize.height * 0.5 - this.height * 0.5

      this.mesh.scale.z = 0.0001
    }
  }

  _setPosition() {
    const offset = getTargetOffset(this.$target)

    this.meshOffset.x = offset.left
    this.meshOffset.y = offset.top

    this.uniforms.uMeshOffset.value.x = this.originPos.x + this.meshOffset.x
    this.uniforms.uMeshOffset.value.y = this.originPos.y - (this.meshOffset.y - Common.scroll.currentScroll)
    this.uniforms.uScroll.value = Common.scroll.currentScroll - this.startParallax
  }

  get startBefore() {
    return Math.max(this.parallaxSectionTop - this.wh * 2, 0)
  }

  get endBefore() {
    return this.parallaxSectionTop
  }

  get progressBefore() {
    if (this.startBefore === 0 && Common.scroll.currentScroll === 0) {
      return 1
    } else {
      let progress = (Common.scroll.currentScroll - this.startBefore) / (this.endBefore - this.startBefore)
      progress = clamp(progress, 0, 1)
      return progress
    }
  }

  get startParallax() {
    return Math.max(this.parallaxSectionTop - this.wh, 0)
  }

  get endParallax() {
    return this.parallaxSectionTop + this.parallaxSectionHeight
  }

  get progressParallax() {
    let progress = (Common.scroll.currentScroll - this.startParallax) / (this.endParallax - this.startParallax)
    progress = clamp(progress, 0, 1)
    return progress
  }

  get startSticky() {
    return Math.max(this.parallaxSectionTop, 0)
  }

  get endSticky() {
    return this.parallaxSectionTop + this.parallaxSectionHeight
  }

  get progressSticky() {
    let progress = (Common.scroll.currentScroll - this.startSticky) / (this.endSticky - this.startSticky)
    progress = clamp(progress, 0, 1)
    return progress
  }

  get startCoverParallax() {
    return Math.max(this.parallaxCoverTriggerTop - this.wh, 0)
  }

  get endCoverParallax() {
    return this.parallaxCoverTriggerTop + (this.endParallax - this.startCoverParallax) * 0.5
  }

  get progressCoverParallax() {
    let progress = (Common.scroll.currentScroll - this.startCoverParallax) / (this.endCoverParallax - this.startCoverParallax)
    progress = clamp(progress, 0, 1)
    return progress
  }

  setParallax() {
    this.progressLerpParallax = lerp(this.progressLerpParallax, this.progressParallax, this.effectInfo.lerpParallax / Common.raf.fpsMultiplier)

    this.uniforms.uParallaxProgress.value = this.progressParallax
    this.uniforms.uParallaxProgressLerp.value = this.progressLerpParallax
    this.uniforms.uStickyProgress.value = this.progressSticky

    if (this.$parallaxCoverTrigger) {
      this.progressLerpCoverParallax = lerp(this.progressLerpCoverParallax, this.progressCoverParallax, this.effectInfo.lerpCoverParallax / Common.raf.fpsMultiplier)
      this.uniforms.uParallaxCoverProgress.value = this.progressLerpCoverParallax
    }

    this.uniforms.uParallaxBeforeProgress.value = this.progressBefore || 0
  }

  fvAnimation() {
    gsap.to(this.uniforms.uFvInViewScale, {
      value: 0,
      duration: 3,
      ease: 'expo.out',
    })
  }

  _setBaseSize() {
    super._setBaseSize()

    this.wh = window.innerHeight
    if (this.$parallaxSection) {
      const parallaxSectionRect = this.$parallaxSection.getBoundingClientRect()
      this.parallaxSectionTop = getTargetOffsetTop(this.$parallaxSection)
      this.parallaxSectionHeight = parallaxSectionRect.height
    }

    if (this.$parallaxCoverTrigger) {
      this.parallaxCoverTriggerTop = getTargetOffsetTop(this.$parallaxCoverTrigger)
    }

    this.uniforms.uStickyAmount.value = this.endSticky - this.startSticky
  }

  onResize() {
    this._setBaseSize()
    this.setResolutions()
    this._setPosition()
  }

  onRaf() {
    this._setPosition()
    this.setParallax()

    if (Common.iomEnable && this.isInView) {
    }
  }
}